import { tenantApi as api } from '../config/api'
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    listArchivedUsers: build.query<ListArchivedUsersApiResponse, ListArchivedUsersApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/archive/users`,
        params: {
          query: queryArg.query,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
          page: queryArg.page,
          size: queryArg.size,
        },
      }),
    }),
    archiveUsers: build.mutation<ArchiveUsersApiResponse, ArchiveUsersApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/archive/users`,
        method: 'PUT',
        body: queryArg.archiveUsersDto,
      }),
    }),
    createOrUpgradeGlobalAdmin: build.mutation<CreateOrUpgradeGlobalAdminApiResponse, CreateOrUpgradeGlobalAdminApiArg>(
      {
        query: (queryArg) => ({
          url: `/tenants/${queryArg.tenantId}/global-admins`,
          method: 'POST',
          body: queryArg.createGlobalAdminDto,
        }),
      },
    ),
    importUsers: build.mutation<ImportUsersApiResponse, ImportUsersApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/import/users`,
        method: 'POST',
        body: queryArg.bodyImportUsers,
      }),
    }),
    unarchiveUsers: build.mutation<UnarchiveUsersApiResponse, UnarchiveUsersApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/unarchive/users`,
        method: 'PUT',
        body: queryArg.userIds,
      }),
    }),
    listTenantUsers: build.query<ListTenantUsersApiResponse, ListTenantUsersApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users`,
        params: {
          roles: queryArg.roles,
          locations: queryArg.locations,
          departments: queryArg.departments,
          positions: queryArg.positions,
          units: queryArg.units,
          cycle_ids: queryArg.cycleIds,
          statuses: queryArg.statuses,
          add_cycles: queryArg.addCycles,
          query: queryArg.query,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
          page: queryArg.page,
          size: queryArg.size,
        },
      }),
    }),
    createUser: build.mutation<CreateUserApiResponse, CreateUserApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users`,
        method: 'POST',
        body: queryArg.createUserDto,
      }),
    }),
    listUserFilters: build.query<ListUserFiltersApiResponse, ListUserFiltersApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users-view/filters`,
        params: { type: queryArg['type'], search: queryArg.search, page: queryArg.page, size: queryArg.size },
      }),
    }),
    assignUsersToUnits: build.mutation<AssignUsersToUnitsApiResponse, AssignUsersToUnitsApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users/assign_to_units`,
        method: 'POST',
        body: queryArg.assignments,
      }),
    }),
    bulkUnassignUsersFromUnits: build.mutation<BulkUnassignUsersFromUnitsApiResponse, BulkUnassignUsersFromUnitsApiArg>(
      {
        query: (queryArg) => ({
          url: `/tenants/${queryArg.tenantId}/users/bulk-unassign-units`,
          method: 'POST',
          body: queryArg.assignments,
        }),
      },
    ),
    inviteUsers: build.mutation<InviteUsersApiResponse, InviteUsersApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users/invite`,
        method: 'POST',
        body: queryArg.ids,
        params: { allow_inactive: queryArg.allowInactive },
      }),
    }),
    unassignUsersFromUnits: build.mutation<UnassignUsersFromUnitsApiResponse, UnassignUsersFromUnitsApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users/unassign-from-units`,
        method: 'POST',
        body: queryArg.assignments,
      }),
    }),
    archiveUser: build.mutation<ArchiveUserApiResponse, ArchiveUserApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/users/${queryArg.userId}`, method: 'DELETE' }),
    }),
    getUserById: build.query<GetUserByIdApiResponse, GetUserByIdApiArg>({
      query: (queryArg) => ({ url: `/tenants/${queryArg.tenantId}/users/${queryArg.userId}` }),
    }),
    updateUser: build.mutation<UpdateUserApiResponse, UpdateUserApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users/${queryArg.userId}`,
        method: 'PUT',
        body: queryArg.updateUserDto,
      }),
    }),
    updateUserSettings: build.mutation<UpdateUserSettingsApiResponse, UpdateUserSettingsApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users/${queryArg.userId}/settings`,
        method: 'PATCH',
        body: queryArg.updateUserSettingsDto,
      }),
    }),
    updateUserStatus: build.mutation<UpdateUserStatusApiResponse, UpdateUserStatusApiArg>({
      query: (queryArg) => ({
        url: `/tenants/${queryArg.tenantId}/users/${queryArg.userId}/update-user-status`,
        method: 'PATCH',
        body: queryArg.updateUserStatusDto,
      }),
    }),
    getCurrentUser: build.query<GetCurrentUserApiResponse, GetCurrentUserApiArg>({
      query: (queryArg) => ({ url: `/user`, params: { subdomain: queryArg.subdomain } }),
    }),
    listUsers: build.query<ListUsersApiResponse, ListUsersApiArg>({
      query: (queryArg) => ({ url: `/users`, params: { roles: queryArg.roles, query: queryArg.query } }),
    }),
  }),
  overrideExisting: true,
})
export { injectedRtkApi as enhancedApi }
export type ListArchivedUsersApiResponse = /** status 200 Successful Response */ PageArchivedUsersDto
export type ListArchivedUsersApiArg = {
  tenantId: string
  /** A search string to filter returned archived users */
  query?: string
  /** Field to sort by. Omitted, archived users are returned most recently archived first. Null values sort last in either direction, and text is compared case-insensitively. */
  sortBy?: 'first_name' | 'employee_id' | 'department' | 'location' | 'archived_at'
  /** Sort direction (ascending or descending) */
  sortOrder?: 'asc' | 'desc'
  page?: number
  size?: number
}
export type ArchiveUsersApiResponse = /** status 200 Successful Response */ ArchiveUsersReportDto
export type ArchiveUsersApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  archiveUsersDto: ArchiveUsersDto
}
export type CreateOrUpgradeGlobalAdminApiResponse = /** status 200 Successful Response */ GlobalAdminResponseDto
export type CreateOrUpgradeGlobalAdminApiArg = {
  tenantId: string
  createGlobalAdminDto: CreateGlobalAdminDto
}
export type ImportUsersApiResponse = /** status 200 Successful Response */ ImportUserReportDto
export type ImportUsersApiArg = {
  tenantId: string
  bodyImportUsers: BodyImportUsers
}
export type UnarchiveUsersApiResponse = /** status 200 Successful Response */ void
export type UnarchiveUsersApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  userIds: string[]
}
export type ListTenantUsersApiResponse = /** status 200 Successful Response */ PageUserDto | UserDto[]
export type ListTenantUsersApiArg = {
  tenantId: string
  /** The required roles for the queried users, passed as an array of role names. Returned users will match all given roles. */
  roles?: string[]
  /** The locations to filter the queried users. */
  locations?: string[]
  /** The departments to filter the queried users. */
  departments?: string[]
  /** The positions to filter the queried users. */
  positions?: string[]
  /** The units to filter the queried users. */
  units?: string[]
  /** Keep only users with a live enrolment in any of the given training cycles. Pass '__EMPTY__' to include users with no live enrolment at all; it combines with cycle ids as OR, so both sets come back together. Requires add_cycles=true: without it the filter is not applied and all users are returned. */
  cycleIds?: string[]
  /** The account statuses to filter the queried users. */
  statuses?: UserStatusEnum[]
  /** Toggles adding the cycles field (each user's live training cycles, excluding any whose course the tenant is no longer entitled to) to the returned response */
  addCycles?: boolean
  /** A search string to filter returned users */
  query?: string
  /** Field to sort by (e.g., first_name, last_name, department, location, position, updated_at, status, email, employee_id) */
  sortBy?: string
  /** Sort direction (ascending or descending) */
  sortOrder?: 'asc' | 'desc'
  page?: number
  size?: number
}
export type CreateUserApiResponse = /** status 201 Successful Response */ UserDto
export type CreateUserApiArg = {
  tenantId: string
  createUserDto: CreateUserDto
}
export type ListUserFiltersApiResponse = /** status 200 Successful Response */ PageUserFilterModelDto | Page
export type ListUserFiltersApiArg = {
  tenantId: string
  /** Type of filter to apply */
  type: FilterType
  /** Search string to filter results */
  search?: string
  page?: number
  size?: number
}
export type AssignUsersToUnitsApiResponse = /** status 200 Successful Response */ UsersUnitsAssignmentOutputDto[]
export type AssignUsersToUnitsApiArg = {
  tenantId: string
  assignments: UsersUnitsAssignmentDto[]
}
export type BulkUnassignUsersFromUnitsApiResponse =
  /** status 200 Successful Response */ UsersUnitsAssignmentOutputDto[]
export type BulkUnassignUsersFromUnitsApiArg = {
  tenantId: string
  assignments: UsersUnitsAssignmentDto[]
}
export type InviteUsersApiResponse = /** status 200 Successful Response */ {
  [key: string]: string
}
export type InviteUsersApiArg = {
  tenantId: string
  allowInactive?: boolean
  ids: string[]
}
export type UnassignUsersFromUnitsApiResponse = /** status 200 Successful Response */ UsersUnitsAssignmentOutputDto[]
export type UnassignUsersFromUnitsApiArg = {
  tenantId: string
  assignments: UsersUnitsAssignmentDto[]
}
export type ArchiveUserApiResponse = /** status 204 Successful Response */ void
export type ArchiveUserApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  userId: string
}
export type GetUserByIdApiResponse = /** status 200 Successful Response */ UserDto
export type GetUserByIdApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  userId: string
}
export type UpdateUserApiResponse = /** status 200 Successful Response */ UserDto
export type UpdateUserApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  userId: string
  updateUserDto: UpdateUserDto
}
export type UpdateUserSettingsApiResponse = /** status 200 Successful Response */ UserDto
export type UpdateUserSettingsApiArg = {
  /** Tenant the request is scoped to. */
  tenantId: string
  userId: string
  updateUserSettingsDto: UpdateUserSettingsDto
}
export type UpdateUserStatusApiResponse = /** status 200 Successful Response */ boolean
export type UpdateUserStatusApiArg = {
  userId: string
  /** Tenant the request is scoped to. */
  tenantId: string
  updateUserStatusDto: UpdateUserStatusDto
}
export type GetCurrentUserApiResponse = /** status 200 Successful Response */ GetCurrentUserOutDto
export type GetCurrentUserApiArg = {
  subdomain?: string
}
export type ListUsersApiResponse = /** status 200 Successful Response */ UserDto[]
export type ListUsersApiArg = {
  /** The required roles for the queried users, passed as string with comma-separated role names. Returned users will match all given roles. */
  roles?: string
  /** A search string to filter returned users */
  query?: string
}
export type TrainingCycleStatus = 'planned' | 'running' | 'ended' | 'archived'
export type UserTrainingCycleDto = {
  cycle_id: string
  cycle_status: TrainingCycleStatus
  name: string
}
export type ValidUserRoles =
  | 'lawpilots_admin'
  | 'customer_success'
  | 'global_customer_manager'
  | 'customer_manager'
  | 'participant'
export type UserStatusEnum = 'Active' | 'Inactive' | 'Pending'
export type ArchivedUsersDto = {
  archived_at?: string
  cycles?: UserTrainingCycleDto[]
  department?: string
  email?: string
  email_verified?: boolean
  employee_id?: string
  first_name: string
  id: string
  invited_at?: string
  is_bluecollar?: boolean
  is_external?: boolean
  language_tag: string
  last_name: string
  location?: string
  position?: string
  roles: ValidUserRoles[]
  status: UserStatusEnum
  units?: any[]
  updated_at: string
}
export type PageArchivedUsersDto = {
  items: ArchivedUsersDto[]
  page?: number
  pages?: number
  size?: number
  total?: number
}
export type ValidationError = {
  loc: (string | number)[]
  msg: string
  type: string
}
export type HttpValidationError = {
  detail?: ValidationError[]
}
export type ArchiveUserSkipReason = 'self' | 'externally_managed' | 'keycloak_error' | 'not_found'
export type SkippedUserDto = {
  id: string
  reason: ArchiveUserSkipReason
}
export type ArchiveUsersReportDto = {
  archived: string[]
  skipped: SkippedUserDto[]
}
export type ArchiveUsersDto = {
  user_ids: string[]
}
export type GlobalAdminResponseDto = {
  email: string
  first_name: string
  id: string
  last_name: string
  status: UserStatusEnum
}
export type Detail = {
  detail: string
}
export type CreateGlobalAdminDto = {
  email: string
  first_name: string
  last_name: string
  user_id?: string
}
export type UserImportEvent = 'UserRecordCreated' | 'UserRecordUpdated' | 'UserRecordUnchanged' | 'UserRecordFailed'
export type CsvUnitsWarningCode =
  | 'UNIT_NOT_FOUND'
  | 'UNIT_ASSIGNEMENT_FAILED'
  | 'UNEXPECTED_ERROR'
  | 'UNIT_ALREADY_ASSIGNED'
export type CsvUnitsWarning = {
  unit_name: string
  warning_code: CsvUnitsWarningCode
}
export type UnitImportReportDto = {
  failed_unit_names?: string[]
  no_of_unit_assignments_done?: number
  no_of_unit_assignments_skipped?: number
  warnings?: CsvUnitsWarning[]
}
export type UserDto = {
  cycles?: UserTrainingCycleDto[]
  department?: string
  email?: string
  email_verified?: boolean
  employee_id?: string
  first_name: string
  id: string
  invited_at?: string
  is_bluecollar?: boolean
  is_external?: boolean
  language_tag: string
  last_name: string
  location?: string
  position?: string
  roles: ValidUserRoles[]
  status: UserStatusEnum
  units?: any[]
  updated_at: string
}
export type CsvRowReportDto = {
  import_event: UserImportEvent
  row: number
  unit_report?: UnitImportReportDto
  user: UserDto
}
export type CsvErrorCode =
  | 'EMPTY_CSV'
  | 'PARSING_ERROR'
  | 'FILE_NOT_FOUND'
  | 'UNEXPECTED_ERROR'
  | 'MISSING_REQUIRED_FIELD'
  | 'MISSING_HEADER'
  | 'INVALID_FIELD'
  | 'INVALID_FILE_TYPE'
  | 'FILE_TOO_LARGE'
  | 'USER_ALREADY_ARCHIVED'
export type CsvRowError = {
  code: CsvErrorCode
  column?: string
  import_event: UserImportEvent
  reason?: string
  row: number
}
export type UnitReportSummaryDto = {
  failed_unit_names?: string[]
  total_no_of_unit_assignments_done?: number
  total_no_of_unit_assignments_skipped?: number
  warnings?: CsvUnitsWarning[]
}
export type ImportUserReportDto = {
  created_rows?: CsvRowReportDto[]
  errors?: CsvRowError[]
  grouped_errors?: {
    [key: string]: CsvRowError[]
  }
  no_of_failed_rows?: number
  no_of_successful_rows?: number
  total_no_of_rows?: number
  unchanged_rows?: CsvRowReportDto[]
  unit_report_summary?: UnitReportSummaryDto
  updated_rows?: CsvRowReportDto[]
}
export type BodyImportUsers = {
  delimiter?: string
  encoding?: string
  header_mapping?: string
  /** The csv file that contains users to be imported. */
  users_csv: Blob
}
export type PageUserDto = {
  items: UserDto[]
  page?: number
  pages?: number
  size?: number
  total?: number
}
export type ValidCreateOrUpdateUserRoles = 'customer_manager' | 'participant'
export type CreateUserDto = {
  department?: string
  email?: string
  email_verified?: boolean
  employee_id?: string
  first_name: string
  language_tag?: string
  last_name: string
  location?: string
  position?: string
  roles: ValidCreateOrUpdateUserRoles[]
}
export type UserFilterModelDto = {
  name: string
  value: string
}
export type PageUserFilterModelDto = {
  items: UserFilterModelDto[]
  page?: number
  pages?: number
  size?: number
  total?: number
}
export type Page = {
  items: any[]
  page?: number
  pages?: number
  size?: number
  total?: number
}
export type FilterType = 'department' | 'role' | 'course' | 'position' | 'location' | 'unit'
export type UsersUnitsAssignmentOutputDto = {
  message: string
  status: 'success' | 'failure'
  unit_id?: string
  unit_name?: string
}
export type UsersUnitsAssignmentDto = {
  unit_id?: string
  user_ids: string[]
}
export type UpdateUserDto = {
  department?: string
  employee_id?: string
  first_name?: string
  language_tag?: string
  last_name?: string
  location?: string
  position?: string
  roles?: ValidCreateOrUpdateUserRoles[]
}
export type UpdateUserSettingsDto = {
  language_tag?: string
}
export type UpdateUserStatusEnum = 'Active' | 'Inactive'
export type UpdateUserStatusDto = {
  status: UpdateUserStatusEnum
}
export type SubdomainAccessDto = {
  is_accessible: boolean
  subdomain: string
}
export type GetCurrentUserTokenOutDto = {
  email?: string
  family_name: string
  given_name: string
}
export type GetCurrentUserOutDto = {
  group: string
  roles: string[]
  subdomain_access?: SubdomainAccessDto
  tenant_id: string
  token: GetCurrentUserTokenOutDto
  user_id: string
  username: string
}
export const {
  useListArchivedUsersQuery,
  useArchiveUsersMutation,
  useCreateOrUpgradeGlobalAdminMutation,
  useImportUsersMutation,
  useUnarchiveUsersMutation,
  useListTenantUsersQuery,
  useCreateUserMutation,
  useListUserFiltersQuery,
  useAssignUsersToUnitsMutation,
  useBulkUnassignUsersFromUnitsMutation,
  useInviteUsersMutation,
  useUnassignUsersFromUnitsMutation,
  useArchiveUserMutation,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useUpdateUserSettingsMutation,
  useUpdateUserStatusMutation,
  useGetCurrentUserQuery,
  useListUsersQuery,
} = injectedRtkApi
